<template>
    <div class="tagline-input-wrapper">
        <textarea 
            ref="textareaRef"
            :value="modelValue ?? ''"
            @input="handleInput"
            @click="saveCursorPosition"
            @keyup="saveCursorPosition"
            @focus="saveCursorPosition"
            :placeholder="placeholder"
            class="tagline-textarea"
            :rows="rows"
        ></textarea>
        <button 
            class="emoji-button"
            @click="toggleEmojiPicker"
            type="button"
            title="Insert emoji"
        >
            😀
        </button>
        <div 
            v-if="showPicker" 
            class="emoji-picker"
            @click.stop
        >
            <div class="emoji-search-container">
                <input 
                    ref="emojiSearchInputRef"
                    v-model="emojiSearchQuery"
                    type="text"
                    placeholder="Search emojis..."
                    class="emoji-search-input"
                    @input.stop
                />
            </div>
            <div class="emoji-grid">
                <button
                    v-for="emojiItem in filteredEmojis"
                    :key="emojiItem.emoji"
                    class="emoji-item"
                    @click="insertEmoji(emojiItem.emoji)"
                    type="button"
                    :title="emojiItem.names.join(', ')"
                >
                    {{ emojiItem.emoji }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { EmojiData, emojiData } from '@whatsapp/data/emojis'
import { recordEmojiUsage, getTopEmojis } from '@/utils/emojiUsage'

const props = defineProps<{
    modelValue: string | undefined
    placeholder?: string
    rows?: number
}>()

const emit = defineEmits<{
    'update:modelValue': [value: string]
}>()

const textareaRef = ref<HTMLTextAreaElement | null>(null)
const emojiSearchInputRef = ref<HTMLInputElement | null>(null)
const showPicker = ref(false)
const emojiSearchQuery = ref('')
const cursorPosition = ref(0)
const topEmojis = ref<string[]>([])

// Watch for changes to modelValue and sync textarea value
watch(() => props.modelValue, (newValue) => {
    if (textareaRef.value) {
        const stringValue = newValue ?? ''
        if (textareaRef.value.value !== stringValue) {
            textareaRef.value.value = stringValue
        }
    }
}, { immediate: true })

const filteredEmojis = computed(() => {
    // If no search query, show top 20 most used emojis
    if (!emojiSearchQuery.value.trim()) {
        // Map top emoji strings back to emoji data objects
        return topEmojis.value
            .map(emoji => emojiData.find(item => item.emoji === emoji))
            .filter((item): item is typeof emojiData[0] => item !== undefined)
    }
    
    // If search query exists, filter and prioritize commonly used emojis
    const query = emojiSearchQuery.value.toLowerCase().trim()
    const topEmojisSet = new Set(topEmojis.value) // Create a Set for fast lookup
    
    const filtered = emojiData.filter(item => 
        item.names.some(name => name.toLowerCase().includes(query))
    )
    
    // Sort: commonly used emojis first, then others
    return filtered.sort((a, b) => {
        const aIsTop = topEmojisSet.has(a.emoji)
        const bIsTop = topEmojisSet.has(b.emoji)
        
        if (aIsTop && !bIsTop) return -1 // a comes first
        if (!aIsTop && bIsTop) return 1  // b comes first
        
        // If both are top or both are not, maintain original order
        // Optionally, we could also sort by position in topEmojis
        if (aIsTop && bIsTop) {
            const aIndex = topEmojis.value.indexOf(a.emoji)
            const bIndex = topEmojis.value.indexOf(b.emoji)
            return aIndex - bIndex // Sort by position in topEmojis
        }
        
        return 0
    }).slice(0, 20) // Limit to 20 results
})

const saveCursorPosition = () => {
    if (textareaRef.value) {
        cursorPosition.value = textareaRef.value.selectionStart
    }
}

const toggleEmojiPicker = () => {
    if (showPicker.value) {
        showPicker.value = false
        emojiSearchQuery.value = ''
    } else {
        // Refresh top emojis when opening picker
        topEmojis.value = getTopEmojis()
        showPicker.value = true
        emojiSearchQuery.value = ''
        saveCursorPosition()
        // Focus search input after picker opens
        setTimeout(() => {
            emojiSearchInputRef.value?.focus()
        }, 0)
    }
}

const insertEmoji = (emoji: string) => {
    if (!textareaRef.value) return
    
    // Record emoji usage
    recordEmojiUsage(emoji)
    
    // Update top emojis list
    topEmojis.value = getTopEmojis()
    
    const currentValue = props.modelValue || ''
    const pos = cursorPosition.value
    const newValue = currentValue.substring(0, pos) + emoji + currentValue.substring(pos)
    
    emit('update:modelValue', newValue)
    
    // Update cursor position after emoji
    cursorPosition.value = pos + emoji.length
    
    // Set cursor position after emoji in textarea
    setTimeout(() => {
        if (textareaRef.value) {
            textareaRef.value.selectionStart = textareaRef.value.selectionEnd = cursorPosition.value
            textareaRef.value.focus()
        }
    }, 0)
    
    // Close picker and clear search
    showPicker.value = false
    emojiSearchQuery.value = ''
}

const handleInput = (event: Event) => {
    const target = event.target as HTMLTextAreaElement
    const newValue = target.value
    const cursorPos = target.selectionStart
    
    // Check if "::" was just typed (check last 2 characters before cursor)
    if (cursorPos >= 2 && newValue.substring(cursorPos - 2, cursorPos) === '::') {
        // Remove the "::" and update the value
        const valueWithoutColons = newValue.substring(0, cursorPos - 2) + newValue.substring(cursorPos)
        emit('update:modelValue', valueWithoutColons)
        
        // Update cursor position (moved back by 2 characters)
        setTimeout(() => {
            if (textareaRef.value) {
                const newCursorPos = cursorPos - 2
                textareaRef.value.selectionStart = textareaRef.value.selectionEnd = newCursorPos
                cursorPosition.value = newCursorPos
            }
        }, 0)
        
        // Open emoji picker and focus search
        if (!showPicker.value) {
            topEmojis.value = getTopEmojis()
            showPicker.value = true
            emojiSearchQuery.value = ''
            setTimeout(() => {
                emojiSearchInputRef.value?.focus()
            }, 0)
        } else {
            // If picker is already open, just focus search
            emojiSearchQuery.value = ''
            setTimeout(() => {
                emojiSearchInputRef.value?.focus()
            }, 0)
        }
    } else {
        // Normal input handling
        emit('update:modelValue', newValue)
    }
}

const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement
    if (!target.closest('.emoji-picker') && !target.closest('.emoji-button')) {
        showPicker.value = false
        emojiSearchQuery.value = ''
    }
}

onMounted(() => {
    // Load top emojis on component mount
    topEmojis.value = getTopEmojis()
    document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.tagline-input-wrapper {
    position: relative;
    flex: 1;
    min-width: 200px;
}

.tagline-textarea {
    width: 100%;
    padding: 8px 35px 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    font-family: inherit;
    resize: vertical;
    min-height: 50px;
    box-sizing: border-box;
}

.tagline-textarea:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
}

.emoji-button {
    position: absolute;
    top: 6px;
    right: 6px;
    background: none;
    border: none;
    font-size: 18px;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: background-color 0.2s;
    z-index: 1;
}

.emoji-button:hover {
    background-color: #f0f0f0;
}

.emoji-picker {
    position: absolute;
    top: 40px;
    right: 0;
    background: white;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    padding: 10px;
    max-width: 300px;
    max-height: 300px;
    display: flex;
    flex-direction: column;
}

.emoji-search-container {
    margin-bottom: 10px;
}

.emoji-search-input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    box-sizing: border-box;
}

.emoji-search-input:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.emoji-picker .emoji-grid {
    overflow-y: auto;
    max-height: 230px;
}

.emoji-grid {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 4px;
}

.emoji-item {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    padding: 6px;
    border-radius: 4px;
    transition: background-color 0.2s;
    text-align: center;
}

.emoji-item:hover {
    background-color: #f0f0f0;
}
</style>

